import React, { useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useSheetsSettings } from '../hooks/useSheetsSettings';
import { applyTabNameFormat } from '../utils/syncHelpers';
import SpreadsheetIdEditor from './SpreadsheetIdEditor';
import StatusMapEditor from './StatusMapEditor';

interface SectionCardProps {
  step: number;
  title: string;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ step, title, children }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
        {step}
      </span>
      <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
        {title}
      </h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const SheetsSettingsPage: React.FC = () => {
  const { state, setSpreadsheets, setTabNameFormat, setStatusMap, save, reload } =
    useSheetsSettings();

  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setSaveSuccess(false);
    try {
      await save();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // error surfaced via state.status === 'save-error'
    }
  };

  if (state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-12">
        <ArrowPathIcon className="w-4 h-4 animate-spin" />
        Loading settings…
      </div>
    );
  }

  if (state.status === 'load-error') {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <ExclamationCircleIcon className="w-4 h-4" />
          {state.message}
        </p>
        <button
          type="button"
          onClick={reload}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const { settings } = state;
  const isSaving = state.status === 'saving';
  const hasSaveError = state.status === 'save-error';
  const isDirty = state.status === 'ready' && state.dirty;
  const hasNoSpreadsheets = settings.spreadsheets.length === 0;

  const tabPreview = applyTabNameFormat(settings.tab_name_format, new Date());

  return (
    <div className="space-y-6 pb-16">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Sheets Sync — Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Configure spreadsheet IDs, tab name format, and status mappings.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircleIcon className="w-4 h-4" />
              Saved
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || hasNoSpreadsheets || (!isDirty && !hasSaveError)}
            title={hasNoSpreadsheets ? 'Add at least one spreadsheet before saving' : undefined}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
          >
            {isSaving ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>

      {/* Save error */}
      {hasSaveError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          {state.message}
        </div>
      )}

      {/* Section 1: Spreadsheet IDs */}
      <SectionCard step={1} title="Spreadsheets">
        <SpreadsheetIdEditor
          spreadsheets={settings.spreadsheets}
          onChange={setSpreadsheets}
        />
        {hasNoSpreadsheets && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            Add at least one spreadsheet entry before saving.
          </p>
        )}
      </SectionCard>

      {/* Section 2: Tab name format */}
      <SectionCard step={2} title="Tab Name Format">
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use <code className="font-mono">MMMM</code> for the full month name and{' '}
            <code className="font-mono">YYYY</code> for the four-digit year.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={settings.tab_name_format}
              onChange={(e) => setTabNameFormat(e.target.value)}
              placeholder="MMMM YYYY"
              className="w-48 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Current month resolves to:{' '}
              <span className="font-semibold text-gray-700 dark:text-gray-200">{tabPreview}</span>
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Section 3: Status mapping */}
      <SectionCard step={3} title="Status Mapping">
        <StatusMapEditor
          statusMap={settings.status_map}
          onChange={setStatusMap}
        />
      </SectionCard>
    </div>
  );
};

export default SheetsSettingsPage;
